import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { BarChart3 } from "lucide-react";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const label = (ym) => `${MONTH[Number(ym.slice(5, 7)) - 1]} ${ym.slice(2, 4)}`;

/** Yearly software spend + next-12-month renewal forecast (per currency). */
export function LicenceSpendChart() {
  const { data } = useQuery({ queryKey: ["licence-spend"], queryFn: api.licenceSpendChart });
  const [ccy, setCcy] = useState("");
  const currencies = data?.currencies || [];
  const active = ccy || currencies[0] || "USD";

  const years = useMemo(() => (data?.by_year || [])
    .map((r) => ({ year: String(r.year), total: r.totals[active] || 0, count: r.count }))
    .filter((r) => r.total > 0), [data, active]);
  const forecast = useMemo(() => (data?.renewal_forecast || [])
    .map((r) => ({ month: label(r.month), total: r.totals[active] || 0, count: r.count })), [data, active]);
  const next12 = forecast.reduce((s, r) => s + r.total, 0);

  if (!data || !currencies.length) return null;
  return (
    <div className="border border-border rounded-md bg-white p-4" data-testid="licence-spend-chart">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="size-4 text-primary" />
          <h3 className="font-display font-semibold text-sm">Software spend</h3>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground" data-testid="licence-spend-next12">
            Renewals next 12 months: <b className="mono text-foreground">{fmtMoney(next12)} {active}</b>
          </span>
          {currencies.length > 1 && (
            <Select value={active} onValueChange={setCcy}>
              <SelectTrigger data-testid="licence-spend-currency" className="h-7 w-24 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{currencies.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Spend by year" testid="licence-spend-years" empty={!years.length}>
          <BarChart data={years}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="year" fontSize={11} />
            <YAxis fontSize={11} tickFormatter={(v) => fmtMoney(v)} width={70} />
            <Tooltip formatter={(v, _n, p) => [`${fmtMoney(v)} ${active} · ${p.payload.count} licence${p.payload.count === 1 ? "" : "s"}`, "Spend"]} />
            <Bar dataKey="total" name="Spend" fill="#C8102E" radius={[3, 3, 0, 0]} />
          </BarChart>
        </Panel>
        <Panel title="Renewal forecast — next 12 months" testid="licence-spend-forecast" empty={!next12}>
          <BarChart data={forecast}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="month" fontSize={10} interval={0} />
            <YAxis fontSize={11} tickFormatter={(v) => fmtMoney(v)} width={70} />
            <Tooltip formatter={(v, _n, p) => [`${fmtMoney(v)} ${active} · ${p.payload.count} due`, "Renewals"]} />
            <Bar dataKey="total" name="Renewals" fill="#0A0A0A" radius={[3, 3, 0, 0]} />
          </BarChart>
        </Panel>
      </div>
    </div>
  );
}

function Panel({ title, testid, empty, children }) {
  return (
    <div data-testid={testid}>
      <p className="overline mb-2">{title}</p>
      <div style={{ height: 200 }}>
        {empty ? (
          <p className="text-xs text-muted-foreground h-full grid place-items-center">No costed licences in this range.</p>
        ) : (
          <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
